import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { adminService } from '../services/adminService';

export interface AdminUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  user_metadata: {
    full_name?: string;
    avatar_url?: string;
  };
  app_metadata: {
    provider?: string;
    providers?: string[];
  };
  is_active?: boolean;
  role?: 'admin' | 'user';
  plan?: string;
}

export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  recentUsers: AdminUser[];
  planDistribution?: {
    free: number;
    standard: number;
    pro: number;
    experto: number;
  };
}

/**
 * Admin check: server-side via Edge Function only.
 * No client-side fallback — admin validation must always be server-side.
 */
export const useAdmin = () => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const check = async () => {
      try {
        const result = await adminService.checkAdmin();
        if (!cancelled) setIsAdmin(result);
      } catch {
        if (!cancelled) setIsAdmin(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    check();
    return () => { cancelled = true; };
  }, [user]);

  return { isAdmin, loading };
};

/**
 * Paginated user listing with server-side search.
 */
export const useUsers = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');

  // Debounce search
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const updateSearch = useCallback((value: string) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1); // Reset to first page on new search
    }, 400);
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await adminService.listUsers(page, perPage, debouncedSearch);

      if (result.error) {
        throw new Error(result.error);
      }

      if (result.data) {
        setUsers(result.data.users);
        setTotalCount(result.data.totalCount);
      }
    } catch (err: any) {
      setError(err.message || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, debouncedSearch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const updateUser = async (userId: string, updates: Partial<AdminUser>) => {
    const result = await adminService.updateUser(userId, {
      user_metadata: updates.user_metadata,
    });

    if (result.success) {
      await fetchUsers();
    }

    return result;
  };

  const deleteUser = async (userId: string) => {
    const result = await adminService.deleteUser(userId);

    if (result.success) {
      await fetchUsers();
    }

    return result;
  };

  const banUser = async (userId: string, ban: boolean) => {
    const result = await adminService.banUser(userId, ban);

    if (result.success) {
      await fetchUsers();
    }

    return result;
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

  return {
    users,
    loading,
    error,
    page,
    totalPages,
    totalCount,
    search,
    setPage,
    setSearch: updateSearch,
    refetch: fetchUsers,
    updateUser,
    deleteUser,
    banUser,
  };
};

/**
 * Admin stats fetched server-side via Edge Function.
 * No longer downloads all users to the browser.
 */
export const useAdminStats = () => {
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    activeUsers: 0,
    newUsersToday: 0,
    newUsersThisWeek: 0,
    recentUsers: [],
    planDistribution: { free: 0, standard: 0, pro: 0, experto: 0 },
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);

      const result = await adminService.getStats();

      if (result.error) {
        throw new Error(result.error);
      }

      if (result.data) {
        setStats(result.data);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, refetch: fetchStats };
};
